import {
  CHANGELOG_KIND_LABELS,
  formatReleaseDate,
  type ChangelogItemKind,
  type ChangelogRelease,
} from '@hit-app/convex/changelog';
import { useRef } from 'react';
import { useWhatsNew } from '../lib/whatsNew';
import { useModalDialog } from '../lib/useModalDialog';

const KIND_BADGE_CLASS: Record<ChangelogItemKind, string> = {
  new: 'badge-success',
  improved: 'badge-news',
  fixed: 'badge-warning',
};

/** Zeigt beim App-Start die noch nicht gesehenen Changelog-Einträge als modalen Dialog. */
export function WhatsNewDialog() {
  const { releases, dismiss } = useWhatsNew();

  if (releases.length === 0) return null;

  return <WhatsNewDialogContent releases={releases} onDismiss={dismiss} />;
}

interface WhatsNewDialogContentProps {
  releases: ChangelogRelease[];
  onDismiss: () => void;
}

function WhatsNewDialogContent({ releases, onDismiss }: WhatsNewDialogContentProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const newest = releases[0];
  const olderCount = releases.length - 1;

  useModalDialog(dialogRef);

  const closeDialog = () => dialogRef.current?.close();

  return (
    <dialog
      ref={dialogRef}
      className="whats-new-dialog"
      aria-labelledby="whats-new-title"
      onClose={onDismiss}
      onClick={(event) => {
        // Klick auf den Backdrop trifft das dialog-Element selbst, nicht dessen Inhalt
        if (event.target === dialogRef.current) closeDialog();
      }}
    >
      <header className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 sm:px-8 sm:pt-8">
        <div className="min-w-0">
          <p className="whats-new-eyebrow">Was ist neu</p>
          <h2 id="whats-new-title" className="mt-1 text-2xl tracking-tight text-balance">
            {newest.title}
          </h2>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            {formatReleaseDate(newest.id)}
            {olderCount > 0 &&
              ` · ${olderCount === 1 ? 'ein weiteres Update' : `${olderCount} weitere Updates`} seit deinem letzten Besuch`}
          </p>
        </div>
        <button
          type="button"
          className="whats-new-close relative shrink-0"
          onClick={closeDialog}
          aria-label="Schliessen"
        >
          <svg viewBox="0 0 16 16" fill="currentColor" className="size-4" aria-hidden="true">
            <path d="M5.28 4.22a.75.75 0 0 0-1.06 1.06L6.94 8l-2.72 2.72a.75.75 0 1 0 1.06 1.06L8 9.06l2.72 2.72a.75.75 0 1 0 1.06-1.06L9.06 8l2.72-2.72a.75.75 0 0 0-1.06-1.06L8 6.94 5.28 4.22Z" />
          </svg>
          <span
            className="absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2 pointer-fine:hidden"
            aria-hidden="true"
          />
        </button>
      </header>

      <div className="whats-new-scroll px-6 pb-2 sm:px-8">
        {releases.map((release, index) => (
          <section
            key={release.id}
            aria-label={release.title}
            className={index === 0 ? undefined : 'mt-7 border-t border-[var(--color-divider)] pt-6'}
          >
            {index > 0 && (
              <div className="mb-4">
                <h3 className="text-lg text-balance">{release.title}</h3>
                <p className="mt-0.5 text-sm text-[var(--color-text-secondary)]">{formatReleaseDate(release.id)}</p>
              </div>
            )}
            <ul role="list" className="space-y-5">
              {release.items.map((item) => (
                <li key={item.title} className="flex flex-col gap-2 sm:grid sm:grid-cols-[6.5rem_1fr] sm:gap-4">
                  <div>
                    <span className={`badge ${KIND_BADGE_CLASS[item.kind]}`}>{CHANGELOG_KIND_LABELS[item.kind]}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-[var(--color-text-primary)]">{item.title}</p>
                    <p className="mt-0.5 text-pretty text-[var(--color-text-secondary)]">{item.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <footer className="border-t border-[var(--color-divider)] px-6 py-5 sm:px-8">
        <button type="button" className="btn btn-primary w-full sm:w-auto" onClick={closeDialog}>
          Verstanden
        </button>
      </footer>
    </dialog>
  );
}
